import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Zap,
} from "lucide-react";

const HERO_STATS = [
  { label: "Accuracy", value: "99.2%", icon: CheckCircle2, color: "text-emerald-400" },
  { label: "Tests Today", value: "148", icon: Zap, color: "text-yellow-500" },
  { label: "Avg Score", value: "78.4", icon: Activity, color: "text-sky-400" },
];

const GRID_PATTERN_STYLE = {
  backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
  backgroundSize: "32px 32px",
};

export function HeroSection() {
  return (
    <section data-testid="landing-hero" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1753563822047-05432700c247?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtb3RvcmN5Y2xlJTIwZHJpdmluZyUyMHRlc3QlMjB0cmFja3xlbnwwfHx8fDE3NzYzNjUyOTZ8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center" />
      <div className="absolute inset-0 bg-zinc-950/85" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-zinc-950" />
      <div className="absolute inset-0 opacity-[0.03]" style={GRID_PATTERN_STYLE} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-6">
            <span className="flex items-center gap-2 text-xs font-mono text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 px-3 py-1.5 rounded-full uppercase tracking-[0.2em]">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              Sensor-Powered Testing
            </span>
          </div>

          <h1
            data-testid="landing-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[0.95] mb-6"
          >
            Smart 2-Wheeler
            <br />
            <span className="text-yellow-500">Driving Test</span>
            <br />
            System
          </h1>

          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl mb-10">
            Transparent, sensor-based evaluation for RTO driving tests.
            Accelerometer, magnetometer, and GPS data delivers unbiased,
            accurate pass/fail results in real-time.
          </p>

          <div className="flex flex-wrap gap-4">
            <Link
              to="/test"
              data-testid="start-test-btn"
              className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-400 transition-all duration-200 hover:shadow-lg hover:shadow-yellow-500/20"
            >
              Start Test Simulation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/dashboard"
              data-testid="view-dashboard-btn"
              className="inline-flex items-center gap-2 bg-zinc-800 text-white border border-white/10 py-3 px-6 rounded-lg hover:bg-zinc-700 transition-all duration-200"
            >
              View Dashboard
              <LayoutDashboardIcon />
            </Link>
          </div>
        </div>

        {/* Floating metric cards */}
        <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-4">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4 w-44 animate-fade-in"
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400">
                  {stat.label}
                </span>
              </div>
              <span className="text-2xl font-mono font-medium text-white">
                {stat.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LayoutDashboardIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="16" rx="1" />
    </svg>
  );
}
